/* eslint-disable jsx-a11y/no-static-element-interactions */
/* eslint-disable jsx-a11y/click-events-have-key-events */
import { ChevronDown, Search, X } from "lucide-react";
import React, { forwardRef, useContext, useEffect, useState } from "react";
import { Breadcrumbs, Button, Checkbox, MultiSelect } from "@mantine/core";
import { Navigate } from "react-router-dom";
import { OdpBlockedDatesContext } from "../../providers/BlockedDatesProvider";
import {
  IBlockedDateEmployees,
  OdpBlockedDateContextType,
} from "../../@types/blockedDates";
import AuditTimestamps from "./AuditTimestamps";
import { ItemProps } from "./BlockDatesFilter";
import { AuthContext } from "../../../../providers/AuthProvider";
import DirectoryTable from "../../../../themes/components/DirectoryTable";
import { ConfirmUpdateBlockedDates } from "./ConfirmUpdateBlockedDates";
import { updateButtonOnClick } from "./EditBlockedDates";
import { BlockedDatesAppliedSuccessfully } from "./BlockedDatesAppliedSuccessfully";
import DownloadBlockedEmployees from "./DownloadBlockedEmployees";

interface IIndividualBlockDateEmployeesProps {
  searchedEmployees: ItemProps[];
  setSearchedEmployees: React.Dispatch<React.SetStateAction<ItemProps[]>>;
  setActiveBlockDateTab: (
    tab: "overview" | "calendar" | "edit" | "history"
  ) => void;
  setEmployeeIdForHistory?: (id: number, name: string) => void;
}

interface IEmployeeData extends IBlockedDateEmployees {
  id: number;
}

const SelectItem = forwardRef<HTMLDivElement, ItemProps>(
  ({ selected, ...others }, ref) => (
    <div
      {...others}
      ref={ref}
      id={others.id.toString()}
      className="flex items-center gap-3 px-2 py-2 rounded cursor-pointer hover:bg-gray-100"
    >
      <Checkbox checked={selected} size="xs" />
      {others.employee_number || others.employee_id_number} -{" "}
      {others.employee_name}
    </div>
  )
);
SelectItem.displayName = "SelectItem";

const ValueItem = forwardRef<HTMLDivElement, ItemProps>(
  ({ value, onSelect, ...others }, ref) => (
    <div
      id={others.id.toString()}
      ref={ref}
      className="flex items-center gap-2 px-2 py-1 mx-1 text-xs text-white rounded-full cursor-pointer bg-primary-base"
    >
      <span>{value}</span>
      <X onClick={onSelect} size={14} />
    </div>
  )
);
ValueItem.displayName = "ValueItem";

const EditIndividualBlockDateEmployees: React.FC<
  IIndividualBlockDateEmployeesProps
> = ({
  searchedEmployees,
  setSearchedEmployees,
  setActiveBlockDateTab,
  setEmployeeIdForHistory,
}) => {
  const auth = useContext(AuthContext);

  const {
    currentBlockedDate,
    updateOrCreateNewBlockDates,
    updateOrCreateEmployeesBlockedDates,
    isUpdatedSuccessfully,
    setIsUpdatedSuccessfully,
    isUpdatingLoading,
  } = React.useContext(OdpBlockedDatesContext) as OdpBlockedDateContextType;

  const [selection, setSelection] = useState<IEmployeeData[]>([]);

  const [isConfirmingUpdate, setIsConfirmingUpdate] = useState(false);

  useEffect(() => {
    setSelection(searchedEmployees.filter((employee) => employee.selected));
  }, [searchedEmployees]);

  useEffect(() => {
    if (searchedEmployees.length === 0) {
      return;
    }

    const selectedEmployees = searchedEmployees.filter(
      (employee) => employee.selected
    );
    setSelection(selectedEmployees as IEmployeeData[]);
  }, [searchedEmployees]);

  const filter = (value: string, selected: boolean, item: ItemProps): boolean =>
    item.employee_name.toLowerCase().includes(value.toLowerCase()) ||
    item.employee_name.includes(value.toLowerCase()) ||
    item.employee_number?.includes(value) ||
    item.mobile_number?.toLowerCase().includes(value.toLowerCase());

  const removeEmployees = (): void => {
    setIsConfirmingUpdate(true);
  };

  if (!auth) {
    return <Navigate replace to="/login" />;
  }

  return (
    <div>
      <BlockedDatesAppliedSuccessfully
        isOpen={isUpdatedSuccessfully}
        setIsOpen={setIsUpdatedSuccessfully}
        setIsCreateNew={(isOpen: boolean) => isOpen}
        lang={{
          title: "Employee successfully removed",
          subtitle: "",
          description:
            "They will no longer be blocked from transacting during this blocked date.",
        }}
      />
      <ConfirmUpdateBlockedDates
        isOpen={isConfirmingUpdate}
        setOpen={setIsConfirmingUpdate}
        lang={{
          title: "Remove this employee from your blocked date?",
          description:
            "They will be removed from this blocked date and all recurrences. They will be able to make transactions during this period, unless they have been added to other blocked dates for the same period.",
          confirm: "Remove",
          cancel: "Cancel",
        }}
        updateBlockedDate={() => {
          updateButtonOnClick(
            true,
            [],
            currentBlockedDate.employees.filter((emp) =>
              selection.some(
                (selected) => selected.employee_id === emp.employee_id
              )
            ),
            currentBlockedDate,
            updateOrCreateNewBlockDates,
            updateOrCreateEmployeesBlockedDates,
            setIsConfirmingUpdate
          );
        }}
      />
      <Breadcrumbs
        separator=">"
        styles={{
          root: {
            margin: "20px 0",
          },
          breadcrumb: {
            cursor: "pointer",
            "&:not(:last-of-type)": {
              opacity: 0.5,
            },
          },
          separator: {
            opacity: 0.5,
          },
        }}
      >
        {[
          { title: "Overview", href: "#", onClick: setActiveBlockDateTab },
          {
            title: `${currentBlockedDate?.title} (${currentBlockedDate.employees.length})`,
            href: "#",
          },
        ].map((item) => (
          <button
            key={item.title}
            onClick={(event) => {
              event.preventDefault();
              if (item.onClick) {
                item.onClick("overview");
              }
            }}
            className="p-0 m-0 bg-transparent border-0 appearance-none cursor-pointer text-inherit font-inherit"
            type="button"
          >
            {item.title}
          </button>
        ))}
      </Breadcrumbs>
      {(currentBlockedDate?.created_at || currentBlockedDate?.updated_at) && (
        <div style={{ marginBottom: 12 }}>
          <AuditTimestamps
            created_at={currentBlockedDate.created_at}
            created_by_name={currentBlockedDate.created_by_name}
            updated_at={currentBlockedDate.updated_at}
            updated_by_name={currentBlockedDate.updated_by_name}
          />
        </div>
      )}
      <div className="flex flex-row items-center justify-between">
        <MultiSelect
          className="w-full max-w-[504px]"
          data={searchedEmployees}
          placeholder="Filter by employee name or number"
          searchable
          filter={filter}
          value={searchedEmployees
            .filter((employee) =>
              selection.some((sel) => sel.employee_id === employee.employee_id)
            )
            .map((employee) => employee.value)}
          itemComponent={SelectItem}
          valueComponent={ValueItem}
          rightSection={<ChevronDown size={14} />}
          icon={<Search size={14} />}
          onChange={(value) => {
            setSearchedEmployees((prev) => {
              const newSelected = new Set(value);
              return prev.map((employee) => ({
                ...employee,
                selected: newSelected.has(employee.value),
              }));
            });
          }}
        />
        <DownloadBlockedEmployees currentBlockDate={currentBlockedDate} />
      </div>

      {currentBlockedDate && (
        <DirectoryTable<IEmployeeData>
          headers={[
            "Full Name",
            "Employee Number",
            "Employee ID",
            "Branch",
            "Actions",
          ]}
          data={currentBlockedDate.employees.map((it) => ({
            id: it.employee_id,
            ...it,
          }))}
          rows={
            currentBlockedDate.employees.map((it) => ({
              name: it.employee_name,
              employee_number: it.employee_number || "N/A",
              employee_id: it.employee_id,
              employee_branch: it.employee_branch || "N/A",
              actions: (
                <div className="flex gap-2">
                  <Button
                    size="xs"
                    variant="outline"
                    onClick={removeEmployees}
                    disabled={!selection.length || isUpdatingLoading}
                    loading={
                      isUpdatingLoading &&
                      selection.some((sel) =>
                        currentBlockedDate.employees.some(
                          (emp) => emp.employee_id === sel.employee_id
                        )
                      )
                    }
                  >
                    Remove
                  </Button>
                  {setEmployeeIdForHistory && (
                    <Button
                      size="xs"
                      variant="outline"
                      onClick={() => {
                        setEmployeeIdForHistory(
                          it.employee_id,
                          it.employee_name
                        );
                        setActiveBlockDateTab("history");
                      }}
                    >
                      History Log
                    </Button>
                  )}
                </div>
              ),
            })) || []
          }
          onSelect={setSelection}
          selection={selection}
        />
      )}
    </div>
  );
};

export default EditIndividualBlockDateEmployees;
